import { TriangleAlert } from "lucide-react";
import {
  ITEM_UNIT_ABBREVIATION,
  ITEM_UNIT_LABEL,
  ItemUnitSchema,
  type ItemUnit,
} from "@forgepoint/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getItemUnitOptions } from "@/modules/items/itemUnitOptions";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { GHOST_FIELD_CLASSES } from "./GhostInput";

const NO_PRICE_MESSAGE = "No price in this unit. Set a unit price override.";

interface LineUnitCellProps {
  value: ItemUnit | null | undefined;
  itemUnit: ItemUnit;
  onChange: (next: ItemUnit | null) => void;
  needsPriceOverride: boolean;
  disabled?: boolean;
}

/**
 * Picker for the unit a quote line is priced in. Picking the item's own unit
 * clears the override; a warning icon shows when the line needs a manual price.
 */
export function LineUnitCell({
  value,
  itemUnit,
  onChange,
  needsPriceOverride,
  disabled = false,
}: LineUnitCellProps) {
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const resolved = value ?? itemUnit;
  const options = getItemUnitOptions(epicorPrimitiveSupportEnabled, value);

  const handleValueChange = (next: string) => {
    const parsed = ItemUnitSchema.safeParse(next);
    if (!parsed.success) {
      return;
    }
    onChange(parsed.data === itemUnit ? null : parsed.data);
  };

  return (
    <div className="flex items-center justify-center gap-0.5">
      <Select
        value={resolved}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label="Line unit"
          className={cn(
            "h-6 sm:h-8 w-auto gap-0.5 px-1 sm:px-1.5 py-0.5 sm:py-1 text-xs sm:text-sm shadow-none [&>svg]:h-3 [&>svg]:w-3",
            GHOST_FIELD_CLASSES,
            value ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <SelectValue>{ITEM_UNIT_ABBREVIATION[resolved]}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((unit) => (
            <SelectItem key={unit} value={unit}>
              {ITEM_UNIT_LABEL[unit]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {needsPriceOverride && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                aria-label={NO_PRICE_MESSAGE}
                className="inline-flex shrink-0 cursor-help rounded-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <TriangleAlert className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-warning" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{NO_PRICE_MESSAGE}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
